import { Component, inject, OnDestroy, signal } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormField } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { RouterLink } from '@angular/router';
import { SubSink } from 'subsink';
import { AuthApiService } from '../../apiCalls/auth-api.service';
import { CustomSnackbarService } from '../../shared/custom-snackbar.service';
import { LocalStorageKeys } from '../../shared/key-const';
import { strongPasswordValidator } from '../../shared/password-validator';
import { RoutesConst } from '../../shared/route-const';
import { AuthService } from '../auth.service';

interface LoginForm {
  email: FormControl<string>;
  password: FormControl<string>;
}

export interface LoginFormValue {
  email: string;
  password: string;
}

export interface GetToken {
  access_token: string;
  expires_in: number;
  token_type: string;
}

@Component({
  selector: 'app-login',
  templateUrl: './login.html',
  styleUrl: './login.scss',
  imports: [MatIconModule, MatButtonModule, RouterLink, MatFormField, MatInputModule, ReactiveFormsModule],
})
export class Login implements OnDestroy {
  private readonly subs = new SubSink();
  private readonly fb = inject(FormBuilder);
  private readonly authApiSvc = inject(AuthApiService);
  private readonly authSvc = inject(AuthService);
  private readonly customSnackbarSvc = inject(CustomSnackbarService);

  apiErrorMessage = signal<string>('');
  saveState = signal<boolean>(false);

  routesConst = RoutesConst;

  form: FormGroup<LoginForm> = this.fb.nonNullable.group({
    email: [localStorage.getItem(LocalStorageKeys.ExtranetLastUsedUserName) || '', [Validators.required, Validators.email]],
    password: ['', [Validators.required, strongPasswordValidator()]],
  });

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    // Login operation
    this.login();
  }

  /**
   * Performs the password reset operation
   */
  private login(): void {
    const formValue = {
      email: this.form.value.email || '',
      password: this.form.value.password || '',
    } as LoginFormValue;

    this.saveState.set(true);
    this.clearErrorMessage();

    this.subs.sink = this.authApiSvc.loginApiCall(formValue).subscribe({
      next: (c) => this.handleSuccess(c, formValue),
      error: (error) => this.handleError(error),
    });
  }

  /**
   * Handles successful
   */
  private handleSuccess(token: GetToken, formValue: LoginFormValue): void {
    this.clearErrorMessage();
    this.saveState.set(false);
    this.authSvc.login(formValue, token);
  }

  /**
   * Handles errors
   */
  private handleError(error: any): void {
    const errorMessage = error?.message || 'Unknown error occurred';
    this.apiErrorMessage.set(`: ${errorMessage}`);
    console.error('Error:', errorMessage);
    this.customSnackbarSvc.errorSnackbar();
    this.saveState.set(false);
  }

  /**
   * Clears the API error message
   */
  private clearErrorMessage(): void {
    this.apiErrorMessage.set('');
  }

  onKeyDown(event: KeyboardEvent): void {
    console.log(event.key);
    if (event.key === 'Enter') {
      this.onSubmit();
    }
  }

  get password() {
    return this.form.controls.password;
  }
  get email() {
    return this.form.controls.email;
  }
}
